"use client";

import { useEffect, useState, useTransition } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import {
  unlinkArtist,
  updateEventAttraction,
} from "@/app/admin/actions";
import Image from "next/image";
import { useModalZIndex } from "@/lib/use-modal-z-index";
import type { FestivalAttraction } from "@/lib/types";
import { ArtistEditModal } from "./artist-edit-modal";
import { TimeInput } from "./time-input";
import { AppQueryProvider } from "@/app/providers";

interface LineupEntryModalProps {
  eventId: number;
  attraction: FestivalAttraction;
  onClose: () => void;
}

export function LineupEntryModal(props: LineupEntryModalProps) {
  return <AppQueryProvider><LineupEntryModalContent {...props} /></AppQueryProvider>;
}

function LineupEntryModalContent({
  eventId,
  attraction,
  onClose,
}: LineupEntryModalProps) {
  const zIndex = useModalZIndex();
  const [isPending, startTransition] = useTransition();
  const [stage, setStage] = useState(attraction.stage ?? "");
  const [setTime, setSetTime] = useState(attraction.set_time ?? "");
  const [setEndTime, setSetEndTime] = useState(attraction.set_end_time ?? "");
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [showArtistEdit, setShowArtistEdit] = useState(false);
  const queryClient = useQueryClient();
  const router = useRouter();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleSave = () => {
    startTransition(async () => {
      await updateEventAttraction(eventId, attraction.id, {
        stage: stage.trim() || null,
        set_time: setTime.trim() || null,
        set_end_time: setEndTime.trim() || null,
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "events"] });
      router.refresh();
      onClose();
    });
  };

  const handleRemove = () => {
    startTransition(async () => {
      await unlinkArtist(eventId, attraction.id);
      queryClient.invalidateQueries({ queryKey: ["admin", "events"] });
      router.refresh();
      onClose();
    });
  };

  return (
    <>
      <div
        className="fixed inset-0 flex items-center justify-center bg-black/70 p-4"
        style={{ zIndex }}
        onClick={onClose}
      >
        <div
          className="relative flex w-full max-w-sm flex-col overflow-visible border border-neutral-800 bg-surface"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Corner markers */}
          <div className="absolute -left-1 -top-1 h-3 w-3 border-l border-t border-acid" />
          <div className="absolute -right-1 -top-1 h-3 w-3 border-r border-t border-acid" />
          <div className="absolute -bottom-1 -left-1 h-3 w-3 border-b border-l border-acid" />
          <div className="absolute -bottom-1 -right-1 h-3 w-3 border-b border-r border-acid" />

          {/* Header */}
          <div className="flex items-center justify-between gap-4 border-b border-neutral-800 p-5">
            <h2 className="font-mono text-sm font-bold tracking-wider text-white">
              EDIT LINEUP ENTRY
            </h2>
            <button
              onClick={onClose}
              className="shrink-0 text-neutral-500 transition-colors hover:text-white"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4 4L12 12M12 4L4 12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-5 space-y-4">
            {/* Artist info (read-only, with link to edit) */}
            <div className="flex items-center gap-3">
              <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full border border-neutral-700 bg-neutral-800">
                {attraction.image_url ? (
                  <Image
                    src={attraction.image_url}
                    alt={attraction.name}
                    fill
                    className="object-cover"
                    sizes="40px"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center font-mono text-xs text-neutral-400">
                    {attraction.name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-mono text-sm text-neutral-200">
                  {attraction.name}
                </p>
              </div>
              <button
                onClick={() => setShowArtistEdit(true)}
                className="font-mono text-[10px] text-acid/70 transition-colors hover:text-acid"
              >
                EDIT ARTIST
              </button>
            </div>

            {/* Stage */}
            <div className="flex items-center gap-2">
              <label className="w-16 shrink-0 font-mono text-[10px] text-neutral-500">
                STAGE
              </label>
              <input
                type="text"
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                placeholder="Stage name"
                className="min-w-0 flex-1 rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[11px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
              />
            </div>

            {/* Set time */}
            <div className="flex items-center gap-2">
              <label className="w-16 shrink-0 font-mono text-[10px] text-neutral-500">
                TIME
              </label>
              <TimeInput value={setTime} onChange={setSetTime} />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-16 shrink-0 font-mono text-[10px] text-neutral-500">
                END
              </label>
              <TimeInput value={setEndTime} onChange={setSetEndTime} />
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-2 border-t border-neutral-800 p-5">
            {!confirmRemove ? (
              <button
                onClick={() => setConfirmRemove(true)}
                disabled={isPending}
                className="rounded px-3 py-2.5 font-mono text-xs font-bold transition-colors cursor-pointer disabled:opacity-40 border border-red-500/30 text-red-500 hover:bg-red-500/10"
              >
                REMOVE
              </button>
            ) : (
              <button
                onClick={handleRemove}
                disabled={isPending}
                className="rounded px-3 py-2.5 font-mono text-xs font-bold transition-colors cursor-pointer disabled:opacity-40 bg-red-500/20 border border-red-500 text-red-500 hover:bg-red-500/30"
              >
                {isPending ? "REMOVING..." : "CONFIRM REMOVE"}
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={isPending}
              className="flex-1 rounded bg-acid py-2.5 font-mono text-sm font-bold text-black transition-opacity cursor-pointer hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isPending ? "SAVING..." : "SAVE"}
            </button>
          </div>
        </div>
      </div>

      {/* Artist edit modal (stacked on top) */}
      {showArtistEdit && (
        <ArtistEditModal
          artist={{
            id: attraction.id,
            name: attraction.name,
            slug: attraction.slug,
            genres: attraction.genres,
            spotify_id: attraction.spotify_id,
            image_url: attraction.image_url,
          }}
          onClose={() => setShowArtistEdit(false)}
        />
      )}
    </>
  );
}
